/**
 * Боковая навигация макета-эталона: знак продукта, блок рабочего пространства, четыре группы
 * разделов со счётчиками и меню пользователя внизу. Узкий экран (до 700 px) оставляет только
 * иконки — правила макета в workspace.css. Меню обучения, как и страница обучения,
 * только с правом training.use.
 */

import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router';
import { LanguageMenuItems } from '@/components/LanguageSwitcher/LanguageMenuItems';
import { Icon } from '@/components/Icon';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { TrainingMenu } from '@/features/training/TrainingMenu';
import { useSignOut } from '@/hooks/useAuth';
import { storageCount, useCollectedCounters } from '@/hooks/useCollectedCounters';
import { useEventStats } from '@/hooks/useEvents';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeaturesStore } from '@/stores/features';
import { useLanguageStore } from '@/stores/language';
import { useSessionStore } from '@/stores/session';
import { initials } from '@/utils/initials';
import { NAV_GROUPS, type NavItem } from './navigation';

/** Журнал без отбора: тот же запрос статистики, что у «Открытых событий» журнала */
const ALL_EVENTS = {};

/** Счётчики пунктов меню; пока ответа нет или запрос не удался — null, а не 0 */
function useNavCounters(): Record<NonNullable<NavItem['counter']>, number | null> {
	const counters = useCollectedCounters();
	/* Открытые события — новые и в работе за всё время, как «Открытые события» журнала */
	const stats = useEventStats(ALL_EVENTS);
	const byStatus = stats.isSuccess ? stats.data.by_workflow_status : undefined;
	return {
		storages: storageCount(counters.isSuccess ? counters.data : undefined),
		events: byStatus ? byStatus.new + byStatus.in_progress : null,
	};
}

export function Sidebar() {
	const { t } = useTranslation('nav');
	const { t: tCommon } = useTranslation('common');
	const flags = useFeaturesStore((s) => s.flags);
	const canTrain = usePermissions().has('training.use');
	const counts = useNavCounters();
	const visible = (item: NavItem) => !item.feature || flags[item.feature] === true;

	return (
		<aside className="ws-sidebar">
			<Link className="ws-brand" to="/overview" aria-label={tCommon('shell.brandLabel')}>
				<span className="ws-brand-symbol" aria-hidden="true">
					<span />
					<span />
					<span />
					<span />
				</span>
				<span>
					quantum<span className="ws-brand-heavy">hash</span>
					<small>{tCommon('shell.brandTagline')}</small>
				</span>
			</Link>
			<WorkspaceBlock />
			<nav aria-label={tCommon('shell.mainNavigation')}>
				{NAV_GROUPS.map((group) => (
					<div key={group.labelKey} className="ws-nav-group">
						<div className="ws-nav-group-label">{t(group.labelKey)}</div>
						{group.items.filter(visible).map((item) => {
							const count = item.counter ? counts[item.counter] : null;
							const label = t(item.labelKey);
							return (
								<NavLink
									key={item.to}
									to={item.to}
									aria-label={label}
									className={({ isActive }) => (isActive ? 'ws-nav-link ws-active' : 'ws-nav-link')}
								>
									<WorkspaceIcon name={item.icon} />
									<span className="ws-nav-label">{label}</span>
									{count !== null && <span className="ws-nav-count">{count}</span>}
								</NavLink>
							);
						})}
					</div>
				))}
			</nav>
			<div className="ws-sidebar-bottom">
				{canTrain && <TrainingMenu />}
				<UserMenu />
			</div>
		</aside>
	);
}

/**
 * Рабочее пространство макета (index.html:43-47): название компании из её настроек. Без
 * названия блок остаётся с подписью «Рабочее пространство» — ни email, ни ID вместо
 * названия не подставляются.
 */
function WorkspaceBlock() {
	const { t: tCommon } = useTranslation('common');
	const companyName = useSessionStore((s) => s.user?.company_name?.trim() || null);
	const workspace = tCommon('shell.workspaceRoot');
	const title = companyName ?? workspace;

	return (
		<div className="ws-workspace">
			<span className="ws-workspace-icon" aria-hidden="true">
				{title.charAt(0).toUpperCase()}
			</span>
			<span>
				{title}
				{companyName && <small>{workspace}</small>}
			</span>
			<WorkspaceIcon name="lock" className="ws-workspace-lock" />
		</div>
	);
}

/**
 * Пользователь внизу меню (index.html:66-70): имя, под ним должность из профиля, без неё —
 * роль. Меню профиля оформлено как соседнее меню «Обучение» (training-menu макета).
 */
function UserMenu() {
	const { t } = useTranslation('auth');
	const { t: tCommon } = useTranslation('common');
	const { t: tTeam } = useTranslation('team');
	const navigate = useNavigate();
	const signOut = useSignOut();
	const user = useSessionStore((s) => s.user);
	const language = useLanguageStore((s) => s.language);
	if (!user) return null;
	const role = user.role ? tTeam(`roles.${user.role}`) : tTeam('roles.unknown');

	return (
		<MenuPrimitive.Root>
			<MenuPrimitive.Trigger className="ws-user" aria-label={tCommon('shell.userMenu')}>
				<span className="ws-avatar">{initials(user.full_name, user.email)}</span>
				<span>
					{user.full_name ?? user.email}
					<small>{user.job_title?.trim() || role}</small>
				</span>
				<span className="ws-user-language">{language.toUpperCase()}</span>
			</MenuPrimitive.Trigger>
			<MenuPrimitive.Portal>
				<MenuPrimitive.Content className="ws-training-menu" side="top" align="start" sideOffset={8}>
					<LanguageMenuItems />
					<MenuPrimitive.Separator className="ws-training-menu-divider" />
					<MenuPrimitive.Item onSelect={() => navigate('/account')}>
						<Icon.User size={16} />
						{t('userMenu.profile')}
					</MenuPrimitive.Item>
					<MenuPrimitive.Item onSelect={() => navigate('/team/list')}>
						<Icon.Users size={16} />
						{t('userMenu.team')}
					</MenuPrimitive.Item>
					<MenuPrimitive.Item onSelect={() => navigate('/change-password')}>
						<Icon.Key size={16} />
						{t('userMenu.changePassword')}
					</MenuPrimitive.Item>
					<MenuPrimitive.Separator className="ws-training-menu-divider" />
					<MenuPrimitive.Item onSelect={() => signOut.mutate()}>
						<Icon.ExternalLink size={16} />
						{t('userMenu.logOut')}
					</MenuPrimitive.Item>
				</MenuPrimitive.Content>
			</MenuPrimitive.Portal>
		</MenuPrimitive.Root>
	);
}
